import React, {useCallback, useEffect, useState} from 'react';
import {useLocation, useNavigate} from "react-router";
import {useTranslation} from 'react-i18next';
import {AxiosError} from 'axios';
import {Location} from 'history';
import client from 'lib/CommonApi/client';
import {
	callAlertWeb,
	callNativeWeb,
	clickSecurityKeypadEventWeb,
	failEventWeb,
	findPincodeWeb,
	footerShowEventWeb,
	hideProgressWeb,
	showProgressWeb
} from 'common/appInterface';
import {isNullOrBlank} from 'common/utils';
import stringUtils from 'common/stringUtils';

const PincodeVerifyPage: React.FC = () => {
	/** Location State */
	const location = useLocation() as Location<LocationState>;
	/** Navigate */
	const navigate = useNavigate();
	/** 다국어처리 */
	const {t, i18n} = useTranslation();
	
	const screenId = "APPL05";
	const screenName = t("APPL05.title");
	
	const [cardApply] = useState<Partial<CardApplyStep2>>(location.state.apply);
	const [cardInfo] = useState(location.state.info);
	const [cardTerminate] = useState(location.state.storage?.terminate);
	
	const [pincodeKeypad, setPincodeKetpad] = useState("");
	
	/** 뒤로가기 처리 (backKeyEvent_App (APP > WEB)) */
	const callbackBackkeyEvent = useCallback(() => {
		navigate(-1);
	}, [navigate]);
	
	/** 보안키패드 입력요청 (clickSecurityKeypadEvent_Web (WEB > APP)) */
	const clickPincodeInput = useCallback(() => {
		clickSecurityKeypadEventWeb(screenId, screenName, "1", "6", t("APPL05.explan01"));
	}, [screenId, screenName, t]);
	
	// 거래비밀번호 초기화 처리 (findPincode_Web (WEB > APP))
	const clickFindPincode = useCallback(() => {
		findPincodeWeb();
	}, []);
	
	/** 보안키패드 입력 (typingSecurityKeypad_App (APP > WEB)) */
	const callbackTypingSecurityKeypadApp = useCallback((event) => {
		setPincodeKetpad(Array<string>(Number.parseInt(event.detail.length)).fill("*").join(""));
	}, []);
	
	/** 거래비밀번호 검증 */
	const callPincodeVerify = useCallback((keypadPid: string, enteredValue: string) => {
		showProgressWeb();
		client.patch('/api/app/pincode/verify', {
			...location.state.query,
			keypadPid: keypadPid,
			enteredValue: enteredValue
		}).then((response) => {
			// 성공
			if (response.data.code === "00") {
				if (cardApply.callPage === "cardManage") {	// 카드 번호 조회
					client.post('/api/app/manage/card/number', {
						...location.state.query,
						...cardApply
					}).then((response) => {
						if (response.data.code === "00") {
							const result = response.data.response;
							navigate('/manage/card/number',
								{
									state: {
										query: location.state.query,
										info: {
											cardProductCodeName: result.cardProductCodeName,
											cardNo: result.cardNo
										},
										swiperIndex: location.state.swiperIndex,
										tabIndex: "3",
										from: location.pathname
									}
								}
							);
						} else {
							callAlertWeb("ERROR", response.data.code, response.data.message);
						}
					}).catch((e: AxiosError) => {
						callAlertWeb("ERROR", "99", e.message);
					}).finally(() => {
						hideProgressWeb();
					});
				} else if (cardApply.callPage === "cardTerminate") {
					client.post('/api/app/manage/terminate', {
						...{
							pid: location.state.query.pid,
							deviceId: location.state.query.deviceId,
							languageCode: location.state.query.languageCode,
							apiVersion: location.state.query.apiVersion,
							deviceType: location.state.query.deviceType
						},
						alternativeCardNo: cardTerminate?.alternativeCardNo,
						reasons: cardTerminate?.reasons?.sort(
							(a, b) => Number(a.code) - Number(b.code)
						)
					}).then((response) => {
						if (response.data.code === "00") {
							const result = response.data.response;
							navigate(`/terminate/result/${result.reissueYn === "Y" ? "progress" : "complete"}`, {
								state: {
									query: location.state.query,
									info: cardInfo,
									swiperIndex: location.state.swiperIndex,
									tabIndex: location.state.tabIndex,
									from: location.pathname
								}
							});
						} else {
							callAlertWeb("ERROR", response.data.code, response.data.message);
						}
					}).catch((e: AxiosError) => {
						callAlertWeb("ERROR", "99", e.message);
					}).finally(() => {
						hideProgressWeb();
					});
				} else if (cardApply.callPage === "cardTerminateCancel") {
					client.post('/api/app/manage/terminate/cancel', {
						...{
							pid: location.state.query.pid,
							deviceId: location.state.query.deviceId,
							languageCode: location.state.query.languageCode,
							apiVersion: location.state.query.apiVersion,
							deviceType: location.state.query.deviceType
						},
						alternativeCardNo: cardTerminate?.alternativeCardNo
					}).then((response) => {
						if (response.data.code === "00") {
							navigate('/terminate/result/cancel', {
								state: {
									query: location.state.query,
									info: cardInfo,
									swiperIndex: location.state.swiperIndex,
									tabIndex: location.state.tabIndex,
									from: location.pathname
								}
							});
						} else {
							callAlertWeb("ERROR", response.data.code, response.data.message);
						}
					}).catch((e: AxiosError) => {
						callAlertWeb("ERROR", "99", e.message);
					}).finally(() => {
						hideProgressWeb();
					});
				} else {	// 카드신청내역 등록
					client.post('/api/app/apply/history', {
						...location.state.query,
						...cardApply,
						receiveStatusCode: "02"
					}).then((response) => {
						if (response.data.code === "00") {
							const result = response.data.response;
							const moveToApplyStep4 = () => {
								navigate('/apply/step4', {
									state: {
										...location.state,
										apply: {
											...cardApply,
											receiveSeq: result.receiveSeq,
											receiveDate: result.receiveDate
										},
										from: location.pathname
									}
								});
							}
							
							client.patch('/api/app/apply/history', {
								...location.state.query,
								receiveSeq: result.receiveSeq,
								receiveDate: result.receiveDate,
								receiveStatusCode: "04",
								phoneNo: cardApply.phoneNo,
								cardProductCode: cardApply.cardProductCode
							}).then(() => {
								client.get('/api/app/apply/info/approval/date', {
									params: location.state.query
								}).then((response) => {
									if (response.data.code === "00") {
										let betweenDays = 1000;
										const approvalDate = response.data.response.kycApprovalDate;
										if (!isNullOrBlank(approvalDate)) {
											console.log("ApprovalDate=" + approvalDate);
											betweenDays = stringUtils.betweenDays(approvalDate);
											console.log("BetweenDays=" + betweenDays);
										}
										
										if (betweenDays <= 180) {
											navigate('/apply/step5', {
												state: {
													...location.state,
													apply: {
														...cardApply,
														receiveSeq: result.receiveSeq,
														receiveDate: result.receiveDate
													},
													from: location.pathname
												}
											});
										} else {
											moveToApplyStep4();
										}
									} else {  // 실패값 응답시에는 step4(신분증업로드) 화면으로 이동
										moveToApplyStep4();
									}
								}).catch((e: AxiosError) => { // 오류 발생시 step4(신분증업로드) 화면으로 이동
									moveToApplyStep4();
								});
							}).catch((e: AxiosError) => { // 오류 발생시 step4(신분증업로드) 화면으로 이동
								moveToApplyStep4();
							}).finally(() => {
								hideProgressWeb();
							});
						} else {
							callAlertWeb("ERROR", response.data.code, response.data.message);
						}
					}).catch((e: AxiosError) => {
						callAlertWeb("ERROR", "99", e.message);
					}).finally(() => {
						hideProgressWeb();
					});
				}
			} else {
				hideProgressWeb();
				setPincodeKetpad("");
				failEventWeb(response.data.code, response.data.message);
				// clickPincodeInput();
			}
		}).catch((e: AxiosError) => {
			hideProgressWeb();
			callAlertWeb("ERROR", "99", e.message);
		});
	}, [location, navigate, cardApply, cardInfo, cardTerminate]);
	
	/** 거래비밀번호 입력완료 처리 (completedPincode_App (APP > WEB)) */
	const callbackCompletedSecurityKeypadApp = useCallback((event) => {
		// setPincodeKetpad(Array<string>(6).fill("*").join(""));
		callPincodeVerify(event.detail.keypadPid, event.detail.enteredValue);
	}, [callPincodeVerify]);
	
	// 최초실행
	useEffect(() => {
		// 언어설정
		i18n.changeLanguage(location.state.query.languageCode);
		// callNative_Web 처리 (WEB > APP)
		callNativeWeb(screenId, screenName);
		// 하단바 숨기기
		footerShowEventWeb("N");
		
		console.log("QUERY STATE");
		console.log(location.state);
		console.log(cardApply);
		
		hideProgressWeb();
		clickPincodeInput();
		
		// 최초한번실행 useEffect() Warning 제거
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);
	
	// 뒤로가기 이벤트 리스너 (backKeyEvent_App (APP > WEB))
	useEffect(() => {
		document.addEventListener("backpress", callbackBackkeyEvent);
		return () => document.removeEventListener("backpress", callbackBackkeyEvent);
	}, [callbackBackkeyEvent]);
	
	// 보안키패드 입력 이벤트 리스너 (typingSecurityKeypad_App (APP > WEB))
	useEffect(() => {
		document.addEventListener("typingKeypad", callbackTypingSecurityKeypadApp);
		return () => document.removeEventListener("typingKeypad", callbackTypingSecurityKeypadApp);
	}, [callbackTypingSecurityKeypadApp]);
	
	// 보안키패드 결과전달 이벤트 리스너 (completedSecurityKeypad_App (APP > WEB))
	useEffect(() => {
		document.addEventListener("completKeypad", callbackCompletedSecurityKeypadApp);
		return () => document.removeEventListener("completKeypad", callbackCompletedSecurityKeypadApp);
	}, [callbackCompletedSecurityKeypadApp]);
	
	
	// TODO 로컬테스트용 임시 입력처리
	const testCompletedPincodeApp = useCallback(() => {
		// let event = new CustomEvent("completKeypad", { detail: { keypadPid: "9C9A4BD7AE17489EB50B49C48266E12E", keypadCode: "1", enteredValue: "b22e97872340658d74a9544a2d1264d8584272d7619fe2caa47a7dfd70c41f9b5233499309818b24573ecc2ec963e966c9e5854b58b93ae3988aff303ecb71cee8f22e84ef7ec05a1f205c89bd983e3135092c8a83169797706ef06cdbcdb8b791fee7785089e00f6ab2d834c31aa8efae67dc1c" }});
		let event = new CustomEvent("completKeypad", {
			detail: {
				keypadPid: "TEST_KEYPAD_PID",
				keypadCode: "1",
				enteredValue: "TEST_ENTERED_VALUE"
			}
		});
		document.dispatchEvent(event);
	}, []);
	
	return (
		<div id="wrap">
			<div id="container" className="pcHidden">
				<section className="contents passWrap pb64-mb">
					<div className="singUpWrapSub mgt10">
						<div className="inputBoxTy01">
							<label className="mgb0" onClick={clickPincodeInput}>
								<input placeholder=" " type="password" value={pincodeKeypad} maxLength={6} readOnly
									   className="inputReadOnly"/>
								<span>{t("APPL05.explan01")}</span>
								<p className="message" onClick={e => e.stopPropagation()}>{t("APPL05.explan02")}</p>
							</label>
							<div className="txtWrap">
								<p className="find_code">
									<span onClick={clickFindPincode} style={{
										color: "#b2b2b2",
										textDecoration: "underline",
										textUnderlinePosition: "under"
									}}>{t("APPL05.explan03")}</span>
								</p>
							</div>
						</div>
						<div className="noticWrap">
							<dl>
								<dt>※ {t("APPL05.explan04")}</dt>
								<dd>- {t("APPL05.explan05")}</dd>
								<dd>- {t("APPL05.explan06")}</dd>
							</dl>
						</div>
					</div>
				</section>
			</div>
			{process.env.REACT_APP_PROFILE !== "prod" &&
                <button className="btnPrimaryTy01" onClick={testCompletedPincodeApp}><span>TEST 입력</span></button>
			}
		</div>
	);
};

export default PincodeVerifyPage;